import type { RunProgress } from '../types/run.api.types';

import { isUnder } from './nav';

/** Statuses a failed stage can be in — any of these on any declared stage opens the Fix & retry tab. */
const FAILED_STAGE_STATUSES = new Set(['FAILED', 'RECONCILE_FAILED']);
const FAILED_RUN_STATUSES = new Set(['FAILED', 'TERMINATED']);

/**
 * A batch's tabs, in journey order: watch it (and approve its publish — the old Publishing tab is
 * folded into Summary's decision band and Approval/Publish stage panes, 2026-10-16), review its
 * products and decide its gates (Option 2: the decision panel sits on top of Products — there is
 * no separate "Your decision" tab), inspect issues, recover. `to` is the route id each tab navigates to (with `{ runId }` params);
 * `segment` is the URL suffix after `/catalog-v2/runs/<runId>` ('' = Summary). The former
 * standalone "Lineage" tab no longer exists: its listing × stage grid is a view inside Products,
 * and its issues-by-reason view is the Issues tab below. Kept in its own (non-component)
 * module, shared by `RunShell`'s tab strip and any nested page that needs to attribute a crumb to
 * one of these tabs (e.g. the Compare page), so the two can never disagree.
 */
export const RUN_TABS = [
  { value: 'overview', segment: '', to: '/catalog-v2/runs/$runId', labelKey: 'runTabs.overview' },
  {
    value: 'products',
    segment: '/products',
    to: '/catalog-v2/runs/$runId/products',
    labelKey: 'runTabs.products',
  },
  // Insights (was the "Content results" drill-down): always listed, disabled in the strip until
  // content generation has scored coverage — see `RunShell`'s `insightsReady`.
  {
    value: 'insights',
    segment: '/insights',
    to: '/catalog-v2/runs/$runId/insights',
    labelKey: 'runTabs.insights',
  },
  {
    value: 'issues',
    segment: '/issues',
    to: '/catalog-v2/runs/$runId/issues',
    labelKey: 'runTabs.issues',
  },
  {
    value: 'recovery',
    segment: '/recovery',
    to: '/catalog-v2/runs/$runId/recovery',
    labelKey: 'runTabs.recovery',
  },
] as const;

export type RunTab = (typeof RUN_TABS)[number]['value'];

/** True once any declared stage shows a failure — the Fix & retry tab only means something then. */
export function hasFailure(progress: RunProgress): boolean {
  return (
    FAILED_RUN_STATUSES.has(progress.runStatus) ||
    progress.stages.some((s) => FAILED_STAGE_STATUSES.has(s.status))
  );
}

/** Stages that mean content generation is part of this batch — before any of them there is no
 *  scorecard to read (Summary's content card and the Insights tab share this rule). */
const CONTENT_STAGES = new Set(['PRE_ENRICH_SCORE', 'SAMPLE', 'ENRICHMENT', 'POST_ENRICH_SCORE']);

/** True once the batch has reached content generation (any content stage declared). */
export function reachedContent(progress: RunProgress | undefined): boolean {
  return !!progress?.stages.some((s) => CONTENT_STAGES.has(s.stage));
}

/** True once APPROVAL/DELIVERY has been reached (or the run completed) — before that there is no publish result to own a page. */
export function deliveryReached(progress: RunProgress): boolean {
  return (
    progress.runStatus === 'COMPLETED' ||
    !!progress.delivery ||
    progress.stages.some(
      (s) => (s.stage === 'APPROVAL' || s.stage === 'DELIVERY') && s.status !== 'PENDING',
    )
  );
}

/** The run tab the URL is on: a bounded match per sub-path, else Summary (the run root). */
export function activeRunTab(pathname: string, base: string): RunTab {
  const matched = RUN_TABS.find(
    (tab) => tab.segment !== '' && isUnder(pathname, `${base}${tab.segment}`),
  )?.value;
  if (matched) return matched;
  return 'overview';
}

/**
 * Which tabs this batch earns right now (P0 "contextual tabs" — a tab appears only once it
 * means something). Open gates no longer add a tab (Option 2): they are the decision panel on
 * Products, which carries the "Waiting" badge instead. The Images tab was removed (2026-10-16);
 * its old URL redirects to Summary.
 */
export function visibleTabs(progress: RunProgress | undefined, hasIssues: boolean) {
  if (!progress) {
    return RUN_TABS.filter((t) => t.value !== 'recovery' && t.value !== 'issues');
  }
  return RUN_TABS.filter((tab) => {
    if (tab.value === 'issues') return hasIssues;
    if (tab.value === 'recovery') return hasFailure(progress);
    return true;
  });
}
